import { useState, useEffect, useRef } from 'react';

interface FolderDescriptionProps {
  description: string | null;
  onSave: (description: string | null) => Promise<void>;
}

export function FolderDescription({ description, onSave }: FolderDescriptionProps) {
  const [value, setValue] = useState(description || '');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastSavedValue = useRef(description || '');

  // Update local value when prop changes
  useEffect(() => {
    setValue(description || '');
    lastSavedValue.current = description || '';
  }, [description]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  const handleBlur = async () => {
    const trimmedValue = value.trim();
    const newValue = trimmedValue || null;
    const oldValue = lastSavedValue.current || null;

    // Only save if value changed
    if (newValue !== oldValue) {
      setSaving(true);
      try {
        await onSave(newValue);
        lastSavedValue.current = trimmedValue;
      } catch (err) {
        // Revert to last saved value on error
        setValue(lastSavedValue.current);
        console.error('Failed to save description:', err);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Save on Escape (blur the textarea)
    if (e.key === 'Escape') {
      textareaRef.current?.blur();
    }
  };

  return (
    <div className="folder-description">
      <textarea
        ref={textareaRef}
        className="folder-description-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="Describe what this folder contains..."
        rows={1}
        disabled={saving}
      />
      {saving && <span className="folder-description-saving">Saving...</span>}
    </div>
  );
}
